import { useNavigate } from '@tanstack/react-router';
import { Paragraph, Separator, Text, XStack, YStack } from 'tamagui';
import { SkeletonList } from '@mrrmlab/ui';
import { Button } from 'tamagui';
import type { MailAccount } from '@mrrmlab/api-client';
import { useMailAccounts, useReviewQueue } from '../mail/hooks';

export function MailRoute() {
  const navigate = useNavigate();
  const { data: accounts, isLoading } = useMailAccounts();
  const { data: reviewQueue } = useReviewQueue();

  if (isLoading) {
    return (
      <YStack padding="$4" gap="$4" maxWidth={640}>
        <SkeletonList count={3} />
      </YStack>
    );
  }

  if (!accounts?.length) {
    return (
      <YStack padding="$4" gap="$4" maxWidth={640}>
        <Text fontSize="$7" fontWeight="700">Mail</Text>
        <YStack gap="$3" alignItems="center" marginTop="$8">
          <Paragraph color="$color10" textAlign="center">
            Noch kein Mail-Konto konfiguriert.
          </Paragraph>
          <Button onPress={() => navigate({ to: '/einstellungen/mail' })}>
            Zu den Einstellungen
          </Button>
        </YStack>
      </YStack>
    );
  }

  return (
    <YStack padding="$4" gap="$4" maxWidth={640}>
      <XStack justifyContent="space-between" alignItems="center">
        <Text fontSize="$7" fontWeight="700">Mail</Text>
        <XStack gap="$2">
          <Button size="$3" chromeless onPress={() => navigate({ to: '/einstellungen/tags' })}>Tags</Button>
          <Button size="$3" onPress={() => navigate({ to: '/mail/review' })}>
            {reviewQueue?.total ? `Review (${reviewQueue.total})` : 'Review'}
          </Button>
        </XStack>
      </XStack>
      <YStack>
        {accounts.map((account, i) => (
          <YStack key={account.id}>
            {i > 0 && <Separator />}
            <AccountRow
              account={account}
              onOpen={() => navigate({ to: '/mail/$accountId/folders', params: { accountId: account.id } })}
            />
          </YStack>
        ))}
      </YStack>
    </YStack>
  );
}

function AccountRow({
  account,
  onOpen,
}: {
  account: MailAccount;
  onOpen: () => void;
}) {
  return (
    <XStack
      paddingVertical="$3"
      paddingHorizontal="$2"
      alignItems="center"
      gap="$3"
      hoverStyle={{ backgroundColor: '$color3' }}
      borderRadius="$3"
      cursor="pointer"
      onPress={onOpen}
    >
      <YStack flex={1}>
        <Text fontSize="$5" fontWeight="600">{account.label}</Text>
        <Text fontSize="$3" color="$color10">{account.username} · {account.host}</Text>
      </YStack>
      <Text color="$color10" fontSize="$5">›</Text>
    </XStack>
  );
}
